import { useEffect, useState } from "react";
import { ChevronRightIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

type Row = components["schemas"]["WhatsAppConnectionAdminResponse"];
type Connection = Row["connection"];
type TemplateStatus = components["schemas"]["WhatsAppTemplateStatus"];

const statusVariant: Record<string, "default" | "destructive" | "secondary"> = {
  Connected: "default",
  NeedsAttention: "destructive",
  Revoked: "secondary",
};

// Meta's quality rating; anything else (UNKNOWN, null) gets a neutral dot.
const qualityDot: Record<string, string> = {
  GREEN: "bg-emerald-500",
  YELLOW: "bg-amber-500",
  RED: "bg-red-500",
};

// Problems first, so a rejected template is the first thing support sees.
const templateOrder: TemplateStatus[] = ["Rejected", "Disabled", "Paused", "Pending", "NotSubmitted", "Approved"];

export function WhatsAppConnectionCard({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchConnections() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/whatsapp/connections", {
        params: { query: { ClientId: clientId } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load the WhatsApp connection.");
      } else {
        setRows(data ?? []);
      }
      setLoading(false);
    }
    fetchConnections();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>WhatsApp</CardTitle>
        <p className="text-muted-foreground mt-1 text-sm">
          The client's number, connected through Embedded Signup. It sends bill approvals, and invoices and reminders
          for every organisation of the client. With Meta's review of its message templates.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-5" />
          </div>
        ) : error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No WhatsApp number connected</p>
        ) : (
          <div className="divide-y">
            {rows.map((row, i) => (
              <ConnectionDetails key={row.connection.id ?? i} row={row} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConnectionDetails({ row }: { row: Row }) {
  const { connection } = row;
  const templates = [...(connection.templates ?? [])].sort(
    (a, b) => templateOrder.indexOf(a.status ?? "Pending") - templateOrder.indexOf(b.status ?? "Pending"),
  );
  const counts = templateOrder
    .map((status) => ({ status, count: templates.filter((t) => t.status === status).length }))
    .filter((c) => c.count > 0);

  return (
    <div className="space-y-4 py-4 first:pt-0 last:pb-0">
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Number">{connection.displayPhoneNumber ?? "-"}</Field>
        <Field label="Verified name">{connection.verifiedName ?? "-"}</Field>
        <Field label="Status">
          {connection.status ? (
            <Badge variant={statusVariant[connection.status] ?? "secondary"}>{connection.status}</Badge>
          ) : (
            "-"
          )}
          {connection.statusReason && (
            <p className="text-muted-foreground mt-1 text-xs">{connection.statusReason}</p>
          )}
        </Field>
        <Field label="Quality">
          {connection.qualityRating ? (
            <span className="inline-flex items-center gap-2">
              <span
                aria-hidden
                className={cn("size-2 rounded-full", qualityDot[connection.qualityRating] ?? "bg-muted-foreground")}
              />
              {connection.qualityRating}
            </span>
          ) : (
            "-"
          )}
        </Field>
        <Field label="Messaging tier">
          {connection.messagingLimitTier ? <code className="text-xs">{connection.messagingLimitTier}</code> : "-"}
        </Field>
        <Field label="Connected">{formatDateTime(connection.connectedAt)}</Field>
        <Field label="WABA ID">
          <Copyable value={connection.wabaId} />
        </Field>
        <Field label="Twilio account">
          <Copyable value={row.twilioAccountSid} />
        </Field>
        <Field label="Twilio sender">
          <Copyable value={row.senderSid} />
        </Field>
        {connection.revokedAt && <Field label="Revoked">{formatDateTime(connection.revokedAt)}</Field>}
      </dl>

      <Collapsible>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <CollapsibleTrigger
            disabled={templates.length === 0}
            className="group inline-flex items-center gap-1 font-medium disabled:opacity-60"
          >
            <ChevronRightIcon className="size-4 transition-transform group-data-[state=open]:rotate-90" />
            Templates ({templates.length})
          </CollapsibleTrigger>
          {counts.map((c) => (
            <span
              key={c.status}
              className={cn(
                "text-xs tabular-nums",
                c.status === "Rejected" || c.status === "Disabled" ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {c.count} {c.status}
            </span>
          ))}
        </div>
        <CollapsibleContent>
          <ul className="mt-2 divide-y rounded-md border">
            {templates.map((t) => (
              <li key={`${t.key}:${t.language}`} className="px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <span className="min-w-0 truncate">
                    <code className="text-xs">{t.key}</code>
                    <span className="text-muted-foreground ml-2 text-xs">{t.language}</span>
                  </span>
                  <Badge
                    variant={t.status === "Rejected" || t.status === "Disabled" ? "destructive" : "outline"}
                    className="shrink-0 text-xs"
                  >
                    {t.status ?? "-"}
                  </Badge>
                </div>
                {t.rejectionReason && <p className="text-muted-foreground mt-1 text-xs">{t.rejectionReason}</p>}
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function Copyable({ value }: { value?: string | null }) {
  if (!value) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="flex items-start gap-2">
      <span className="font-mono text-xs break-all">{value}</span>
      <CopyButton value={value} className="mt-0.5 shrink-0" />
    </span>
  );
}
